import React, { useState } from "react";
import Headers from "../Component/Headers";
import Subheader from "../Component/Subheader";
import Footer from "../Component/Footer";
import { useNavigate } from "react-router-dom";
import authService from "../services/authService";

function Login_Register({ setUser }) {
  const [activeTab, setActiveTab] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Sign In
    email: "",
    password: "",
    // Sign Up
    fullName: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Handle Sign In
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Basic validation
      if (!formData.email || !formData.password) {
        setErrors({ general: "Please fill in all fields" });
        setLoading(false);
        return;
      }

      const response = await authService.login({
        email: formData.email,
        password: formData.password
      });

      if (response.success) {
        // Store token and user data
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.customer));
        localStorage.setItem("userLogin", "True");
        
        // Update parent component state
        setUser(response.data.customer);
        
        // Navigate to user page
        navigate("/User");
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors({
        general: error.response?.data?.message || "Login failed. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Sign Up
  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validation
      if (!formData.fullName || !formData.email || !formData.password || !formData.confirmPassword) {
        setErrors({ general: "Please fill in all fields" });
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setErrors({ confirmPassword: "Passwords do not match" });
        setLoading(false);
        return;
      }

      const response = await authService.register({
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });

      if (response.success) {
        // Auto login after successful registration
        const loginResponse = await authService.login({
          email: formData.email,
          password: formData.password
        });

        if (loginResponse.success) {
          // Store token and user data
          localStorage.setItem("token", loginResponse.data.token);
          localStorage.setItem("user", JSON.stringify(loginResponse.data.customer));
          
          // Update parent component state
          setUser(loginResponse.data.customer);
          
          // Navigate to user page
          navigate("/User");
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrors({
        general: error.response?.data?.message || "Registration failed. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  // Switch between tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setErrors({});
    // Clear form data when switching tabs
    setFormData({
      email: "",
      password: "",
      fullName: "",
      confirmPassword: ""
    });
  };

  return (
    <div>
      <Subheader />

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-5">
            {/* Shadow Container */}
            <div
              className="p-4 rounded"
              style={{
                backgroundColor: "#fff",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              {/* Tab Selection */}
              <div className="d-flex border-bottom mb-4">
                <button
                  className="flex-grow-1 py-2 text-center"
                  onClick={() => handleTabChange("signin")}
                  style={{
                    border: "none",
                    borderBottom:
                      activeTab === "signin"
                        ? "3px solid #FD7E14"
                        : "3px solid transparent",
                    fontWeight: "600",
                    color: "#343a40",
                    backgroundColor: "transparent",
                    outline: "none",
                  }}
                >
                  Sign In
                </button>
                <button
                  className="flex-grow-1 py-2 text-center"
                  onClick={() => handleTabChange("signup")}
                  style={{
                    border: "none",
                    borderBottom:
                      activeTab === "signup"
                        ? "3px solid #FD7E14"
                        : "3px solid transparent",
                    fontWeight: "600",
                    color: "#343a40",
                    backgroundColor: "transparent",
                    outline: "none",
                  }}
                >
                  Sign Up
                </button>
              </div>

              {/* Error Message */}
              {errors.general && (
                <div className="alert alert-danger" role="alert">
                  {errors.general}
                </div>
              )}

              {/* Forms */}
              {activeTab === "signin" ? (
                <form onSubmit={handleSignIn}>
                  <div className="mb-4">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      name="password"
                      className="form-control"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <div className="text-end mt-1">
                      <a href="/ForgotPassword" style={{ fontSize: "0.875rem", color: "#FD7E14" }}>
                        Forgot Password?
                      </a>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="btn w-100"
                    disabled={loading}
                    style={{
                      backgroundColor: "#FD7E14",
                      color: "#fff",
                      fontWeight: "600",
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    {loading ? "Signing In..." : "Sign In"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSignUp}>
                  <div className="mb-3">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      className="form-control"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      name="password"
                      className="form-control"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                    {errors.confirmPassword && (
                      <div className="invalid-feedback">{errors.confirmPassword}</div>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="btn w-100"
                    disabled={loading}
                    style={{
                      backgroundColor: "#FD7E14",
                      color: "#fff",
                      fontWeight: "600",
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    {loading ? "Creating Account..." : "Sign Up"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login_Register;